
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  apiDelete,
  apiGet,
  apiGetText,
  apiPost,
  apiPut,
} from "../services/apiClient";
import { useToast } from "../components/ToastContext";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";

const emptyLink = () => ({ label: "", url: "" });

const emptyColumn = () => ({ heading: "", links: [emptyLink()] });

const emptyPartner = () => ({
  name: "",
  logo_url: "",
  url: "",
  alt_text: "",
  sort_order: 0,
  is_active: true,
});

const normalizePartners = (items) => {
  if (!Array.isArray(items) || !items.length) return [emptyPartner()];
  return items.map((item, index) => ({
    ...emptyPartner(),
    ...item,
    name: item?.name || item?.label || item?.title || "",
    logo_url: item?.logo_url || item?.logoUrl || item?.logo || "",
    url: item?.url || item?.href || item?.link || "",
    alt_text: item?.alt_text || item?.altText || item?.name || item?.label || "",
    sort_order: Number(item?.sort_order ?? index + 1) || index + 1,
    is_active: item?.is_active !== false,
  }));
};

const sanitizePartners = (items) =>
  normalizePartners(items)
    .map((item, index) => ({
      ...emptyPartner(),
      ...item,
      name: String(item?.name || "").trim(),
      logo_url: String(item?.logo_url || "").trim(),
      url: String(item?.url || "").trim(),
      alt_text: String(item?.alt_text || item?.name || "").trim(),
      sort_order: Number(item?.sort_order ?? index + 1) || index + 1,
      is_active: item?.is_active !== false,
    }))
    .filter((item) => item.name || item.logo_url || item.url);

const buildFooterPayload = (form) => ({
  ...form,
  partners_section: {
    ...form.partners_section,
    eyebrow: String(form.partners_section?.eyebrow || "").trim(),
    heading: String(form.partners_section?.heading || "").trim(),
    description: String(form.partners_section?.description || "").trim(),
    items: sanitizePartners(form.partners_section?.items),
  },
});

const resolveApiBase = () => {
  const envBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "";
  if (typeof window === "undefined") return envBase || "";
  const fallback = window.location.origin || "";
  if (!envBase) return fallback;
  return envBase;
};

const API_BASE = resolveApiBase();

const buildEmptyState = () => ({
  company: {
    name: "",
    description: "",
    email: "",
    phone: "",
    address: "",
  },
  newsletter_card: {
    tag: "",
    heading: "",
    description: "",
    placeholder: "",
    button_label: "",
    compact: {
      tag: "",
      title: "",
      description: "",
      placeholder: "",
      button_label: "",
    },
  },
  cta_card: {
    tag: "",
    heading: "",
    description: "",
    button_label: "",
    button_url: "",
  },
  faq_card: {
    tag: "",
    heading: "",
    description: "",
    search_placeholder: "",
  },
  partners_section: {
    eyebrow: "Partners",
    heading: "As seen with",
    description: "",
    items: [emptyPartner()],
  },
  columns: {
    company: emptyColumn(),
    support: emptyColumn(),
    places: emptyColumn(),
  },
  social_links: {
    instagram: "",
    facebook: "",
    x: "",
    youtube: "",
    linkedin: "",
  },
  copyright_text: "",
});

const buildFaqDraft = () => ({
  question: "",
  answer: "",
  category: "general",
  sort_order: 0,
  is_active: true,
});

const normalizeLinks = (items) => {
  if (!Array.isArray(items) || !items.length) return [emptyLink()];
  return items.map((item) => ({
    label: item?.label || item?.title || "",
    url: item?.url || item?.href || item?.link || "",
  }));
};

const normalizeColumn = (column) => ({
  heading: column?.heading || column?.title || "",
  links: normalizeLinks(column?.links || column?.items || []),
});

const mergeFooter = (payload) => {
  const base = buildEmptyState();
  if (!payload || typeof payload !== "object") return base;
  return {
    ...base,
    ...payload,
    company: { ...base.company, ...(payload.company || {}) },
    newsletter_card: {
      ...base.newsletter_card,
      ...(payload.newsletter_card || {}),
      compact: {
        ...base.newsletter_card.compact,
        ...(payload.newsletter_card?.compact || {}),
      },
    },
    cta_card: { ...base.cta_card, ...(payload.cta_card || {}) },
    faq_card: { ...base.faq_card, ...(payload.faq_card || {}) },
    partners_section: {
      ...base.partners_section,
      ...(payload.partners_section || {}),
      items: normalizePartners(payload.partners_section?.items),
    },
    columns: {
      company: normalizeColumn(payload.columns?.company),
      support: normalizeColumn(payload.columns?.support),
      places: normalizeColumn(payload.columns?.places),
    },
    social_links: { ...base.social_links, ...(payload.social_links || {}) },
  };
};

const ContentFooterPage = ({ token }) => {
  const toast = useToast();
  const [form, setForm] = useState(buildEmptyState());
  const [saving, setSaving] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqSaving, setFaqSaving] = useState(false);
  const [faqCategoryFilter, setFaqCategoryFilter] = useState("all");
  const [showAddFaq, setShowAddFaq] = useState(false);
  const [faqDraft, setFaqDraft] = useState(buildFaqDraft());
  const [faqEditId, setFaqEditId] = useState(null);
  const [faqEditDraft, setFaqEditDraft] = useState(buildFaqDraft());
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberMeta, setSubscriberMeta] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 1,
  });
  const [subscriberLoading, setSubscriberLoading] = useState(false);
  const [partnerUploadingIndex, setPartnerUploadingIndex] = useState(null);

  const categories = useMemo(() => {
    const set = new Set([
      "general",
      "booking",
      "packages",
      "cab",
      "payment",
      "hosting",
      "support",
    ]);
    faqs.forEach((item) => {
      if (item?.category) set.add(item.category);
    });
    return ["all", ...Array.from(set)];
  }, [faqs]);
  const previewHasPartnerCopy = Boolean(
    form.partners_section?.eyebrow ||
      form.partners_section?.heading ||
      form.partners_section?.description
  );

  const loadFooter = useCallback(async () => {
    try {
      const res = await apiGet("/api/admin/content/footer-settings", token);
      const payload = res?.data || res;
      setForm(mergeFooter(payload));
    } catch (err) {
      toast?.error?.(err.message || "Unable to load footer settings.");
    }
  }, [toast, token]);

  const loadFaqs = useCallback(async () => {
    setFaqLoading(true);
    try {
      const params = new URLSearchParams();
      if (faqCategoryFilter && faqCategoryFilter !== "all") {
        params.set("category", faqCategoryFilter);
      }
      const path = params.toString()
        ? `/api/admin/content/faqs?${params.toString()}`
        : "/api/admin/content/faqs";
      const res = await apiGet(path, token);
      const payload = res?.data || res;
      setFaqs(Array.isArray(payload) ? payload : []);
    } catch (err) {
      toast?.error?.(err.message || "Unable to load FAQs.");
    } finally {
      setFaqLoading(false);
    }
  }, [faqCategoryFilter, toast, token]);

  const loadSubscribers = useCallback(async (page = 1) => {
    setSubscriberLoading(true);
    try {
      const res = await apiGet(
        `/api/admin/content/newsletter/subscribers?limit=20&page=${page}`,
        token
      );
      const payload = res?.data && res?.meta ? res : res?.data || res;
      setSubscribers(payload?.data || []);
      setSubscriberMeta(
        payload?.meta || {
          total: 0,
          page: 1,
          limit: 20,
          pages: 1,
        }
      );
    } catch (err) {
      toast?.error?.(err.message || "Unable to load subscribers.");
    } finally {
      setSubscriberLoading(false);
    }
  }, [toast, token]);

  useEffect(() => {
    loadFooter();
  }, [loadFooter]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  useEffect(() => {
    loadSubscribers(1);
  }, [loadSubscribers]);
  const updateColumnLinks = (group, idx, patch) => {
    setForm((prev) => {
      const links = [...(prev.columns[group]?.links || [])];
      links[idx] = { ...links[idx], ...patch };
      return {
        ...prev,
        columns: {
          ...prev.columns,
          [group]: { ...prev.columns[group], links },
        },
      };
    });
  };

  const addColumnLink = (group) =>
    setForm((prev) => ({
      ...prev,
      columns: {
        ...prev.columns,
        [group]: {
          ...prev.columns[group],
          links: [...(prev.columns[group]?.links || []), emptyLink()],
        },
      },
    }));

  const removeColumnLink = (group, idx) =>
    setForm((prev) => {
      const links = (prev.columns[group]?.links || []).filter(
        (_, index) => index !== idx
      );
      return {
        ...prev,
        columns: {
          ...prev.columns,
          [group]: {
            ...prev.columns[group],
            links: links.length ? links : [emptyLink()],
          },
        },
      };
    });

  const updatePartner = (index, patch) => {
    setForm((prev) => {
      const items = normalizePartners(prev.partners_section?.items);
      items[index] = { ...items[index], ...patch };
      return {
        ...prev,
        partners_section: {
          ...prev.partners_section,
          items,
        },
      };
    });
  };

  const addPartner = () =>
    setForm((prev) => ({
      ...prev,
      partners_section: {
        ...prev.partners_section,
        items: [
          ...normalizePartners(prev.partners_section?.items),
          {
            ...emptyPartner(),
            sort_order: normalizePartners(prev.partners_section?.items).length + 1,
          },
        ],
      },
    }));

  const removePartner = (index) =>
    setForm((prev) => {
      const nextItems = normalizePartners(prev.partners_section?.items).filter(
        (_, itemIndex) => itemIndex !== index
      );
      return {
        ...prev,
        partners_section: {
          ...prev.partners_section,
          items: nextItems.length ? nextItems : [emptyPartner()],
        },
      };
    });

  const uploadPartnerLogo = async (index, file) => {
    if (!file) return;
    setPartnerUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/uploads`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: "include",
        body: formData,
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.message || "Failed to upload logo.");
      }
      updatePartner(index, { logo_url: payload?.url || "" });
      toast?.success?.("Partner logo uploaded.");
    } catch (err) {
      toast?.error?.(err.message || "Failed to upload logo.");
    } finally {
      setPartnerUploadingIndex(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payloadToSave = buildFooterPayload(form);
      const res = await apiPut("/api/admin/content/footer-settings", payloadToSave, token);
      const payload = res?.data || res;
      setForm(mergeFooter(payload));
      toast?.success?.("Footer updated.");
    } catch (err) {
      toast?.error?.(err.message || "Failed to update footer.");
    } finally {
      setSaving(false);
    }
  };

  const submitFaq = async () => {
    if (!faqDraft.question.trim()) {
      toast?.error?.("Question is required.");
      return;
    }
    if (!faqDraft.answer.trim()) {
      toast?.error?.("Answer is required.");
      return;
    }
    setFaqSaving(true);
    try {
      await apiPost("/api/admin/content/faqs", faqDraft, token);
      toast?.success?.("FAQ added.");
      setFaqDraft(buildFaqDraft());
      setShowAddFaq(false);
      await loadFaqs();
    } catch (err) {
      toast?.error?.(err.message || "Failed to add FAQ.");
    } finally {
      setFaqSaving(false);
    }
  };

  const startEditFaq = (faq) => {
    setFaqEditId(faq.id || faq._id);
    setFaqEditDraft({
      question: faq.question || "",
      answer: faq.answer || "",
      category: faq.category || "general",
      sort_order: faq.sort_order ?? faq.sortOrder ?? 0,
      is_active: faq.is_active ?? faq.isActive ?? false,
    });
  };

  const cancelEditFaq = () => {
    setFaqEditId(null);
    setFaqEditDraft(buildFaqDraft());
  };

  const updateFaq = async () => {
    if (!faqEditId) return;
    if (!faqEditDraft.question.trim() || !faqEditDraft.answer.trim()) {
      toast?.error?.("Question and answer are required.");
      return;
    }
    setFaqSaving(true);
    try {
      await apiPut(`/api/admin/content/faqs/${faqEditId}`, faqEditDraft, token);
      toast?.success?.("FAQ updated.");
      cancelEditFaq();
      await loadFaqs();
    } catch (err) {
      toast?.error?.(err.message || "Failed to update FAQ.");
    } finally {
      setFaqSaving(false);
    }
  };
  const deleteFaq = async (faq) => {
    if (!window.confirm("Delete this FAQ?")) return;
    setFaqSaving(true);
    try {
      await apiDelete(`/api/admin/content/faqs/${faq.id || faq._id}`, token);
      toast?.success?.("FAQ deleted.");
      await loadFaqs();
    } catch (err) {
      toast?.error?.(err.message || "Failed to delete FAQ.");
    } finally {
      setFaqSaving(false);
    }
  };

  const toggleFaqActive = async (faq) => {
    const nextActive = !(faq.is_active ?? faq.isActive);
    setFaqSaving(true);
    try {
      await apiPut(
        `/api/admin/content/faqs/${faq.id || faq._id}`,
        {
          question: faq.question,
          answer: faq.answer,
          category: faq.category || "general",
          sort_order: faq.sort_order ?? faq.sortOrder ?? 0,
          is_active: nextActive,
        },
        token
      );
      setFaqs((prev) =>
        prev.map((item) =>
          (item.id || item._id) === (faq.id || faq._id)
            ? { ...item, is_active: nextActive }
            : item
        )
      );
      toast?.success?.("FAQ updated.");
    } catch (err) {
      toast?.error?.(err.message || "Failed to update FAQ.");
    } finally {
      setFaqSaving(false);
    }
  };

  const toggleSubscriber = async (subscriber) => {
    const nextActive = !subscriber.is_active;
    try {
      await apiPut(
        `/api/admin/content/newsletter/subscribers/${subscriber.id}`,
        { is_active: nextActive },
        token
      );
      setSubscribers((prev) =>
        prev.map((item) =>
          item.id === subscriber.id
            ? { ...item, is_active: nextActive }
            : item
        )
      );
      toast?.success?.("Subscriber updated.");
    } catch (err) {
      toast?.error?.(err.message || "Failed to update subscriber.");
    }
  };

  const exportSubscribers = async () => {
    try {
      const csv = await apiGetText("/api/admin/content/newsletter/export", token);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "newsletter_subscribers.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      toast?.success?.("Export ready.");
    } catch (err) {
      toast?.error?.(err.message || "Export failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">
          Content
        </p>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Footer Management
            </h1>
            <p className="text-sm text-slate-600">
              Manage footer content and public newsletter settings.
            </p>
          </div>
          <div className="text-sm text-slate-600">
            {subscriberMeta.total} Subscribers
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Company info</h2>
        <input
          className="form-control"
          placeholder="Company name"
          value={form.company.name}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              company: { ...prev.company, name: e.target.value },
            }))
          }
        />
        <textarea
          className="form-control"
          rows="3"
          placeholder="Company description"
          value={form.company.description}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              company: { ...prev.company, description: e.target.value },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Email"
          value={form.company.email}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              company: { ...prev.company, email: e.target.value },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Phone"
          value={form.company.phone}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              company: { ...prev.company, phone: e.target.value },
            }))
          }
        />
        <textarea
          className="form-control"
          rows="2"
          placeholder="Address"
          value={form.company.address}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              company: { ...prev.company, address: e.target.value },
            }))
          }
        />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Footer columns</h2>
        <div className="row g-3">
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Company column heading"
              value={form.columns.company.heading}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  columns: {
                    ...prev.columns,
                    company: {
                      ...prev.columns.company,
                      heading: e.target.value,
                    },
                  },
                }))
              }
            />
            <div className="mt-3 space-y-2">
              {form.columns.company.links.map((link, idx) => (
                <div
                  key={`company-${idx}`}
                  className="row g-2 align-items-center"
                >
                  <div className="col-md-5">
                    <input
                      className="form-control"
                      placeholder="Label"
                      value={link.label}
                      onChange={(e) =>
                        updateColumnLinks("company", idx, {
                          label: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      className="form-control"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) =>
                        updateColumnLinks("company", idx, {
                          url: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-1 d-grid">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => removeColumnLink("company", idx)}
                    >
                      -
                    </button>
                  </div>
                </div>
              ))}
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => addColumnLink("company")}
              >
                Add link
              </button>
            </div>
          </div>

          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Support column heading"
              value={form.columns.support.heading}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  columns: {
                    ...prev.columns,
                    support: {
                      ...prev.columns.support,
                      heading: e.target.value,
                    },
                  },
                }))
              }
            />
            <div className="mt-3 space-y-2">
              {form.columns.support.links.map((link, idx) => (
                <div
                  key={`support-${idx}`}
                  className="row g-2 align-items-center"
                >
                  <div className="col-md-5">
                    <input
                      className="form-control"
                      placeholder="Label"
                      value={link.label}
                      onChange={(e) =>
                        updateColumnLinks("support", idx, {
                          label: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      className="form-control"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) =>
                        updateColumnLinks("support", idx, {
                          url: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-1 d-grid">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => removeColumnLink("support", idx)}
                    >
                      -
                    </button>
                  </div>
                </div>
              ))}
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => addColumnLink("support")}
              >
                Add link
              </button>
            </div>
          </div>

          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Places column heading"
              value={form.columns.places.heading}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  columns: {
                    ...prev.columns,
                    places: {
                      ...prev.columns.places,
                      heading: e.target.value,
                    },
                  },
                }))
              }
            />
            <div className="mt-3 space-y-2">
              {form.columns.places.links.map((link, idx) => (
                <div
                  key={`places-${idx}`}
                  className="row g-2 align-items-center"
                >
                  <div className="col-md-5">
                    <input
                      className="form-control"
                      placeholder="Label"
                      value={link.label}
                      onChange={(e) =>
                        updateColumnLinks("places", idx, {
                          label: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      className="form-control"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) =>
                        updateColumnLinks("places", idx, {
                          url: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-1 d-grid">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => removeColumnLink("places", idx)}
                    >
                      -
                    </button>
                  </div>
                </div>
              ))}
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => addColumnLink("places")}
              >
                Add link
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">
          Curated journeys card
        </h2>
        <input
          className="form-control"
          placeholder="Tag label"
          value={form.newsletter_card.tag}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              newsletter_card: {
                ...prev.newsletter_card,
                tag: e.target.value,
              },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Heading"
          value={form.newsletter_card.heading}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              newsletter_card: {
                ...prev.newsletter_card,
                heading: e.target.value,
              },
            }))
          }
        />
        <textarea
          className="form-control"
          rows="2"
          placeholder="Description"
          value={form.newsletter_card.description}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              newsletter_card: {
                ...prev.newsletter_card,
                description: e.target.value,
              },
            }))
          }
        />
        <div className="row g-2">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Email placeholder"
              value={form.newsletter_card.placeholder}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  newsletter_card: {
                    ...prev.newsletter_card,
                    placeholder: e.target.value,
                  },
                }))
              }
            />
          </div>
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Button label"
              value={form.newsletter_card.button_label}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  newsletter_card: {
                    ...prev.newsletter_card,
                    button_label: e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">
          Custom journeys card
        </h2>
        <input
          className="form-control"
          placeholder="Tag label"
          value={form.cta_card.tag}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              cta_card: { ...prev.cta_card, tag: e.target.value },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Heading"
          value={form.cta_card.heading}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              cta_card: { ...prev.cta_card, heading: e.target.value },
            }))
          }
        />
        <textarea
          className="form-control"
          rows="2"
          placeholder="Description"
          value={form.cta_card.description}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              cta_card: { ...prev.cta_card, description: e.target.value },
            }))
          }
        />
        <div className="row g-2">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Button label"
              value={form.cta_card.button_label}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  cta_card: { ...prev.cta_card, button_label: e.target.value },
                }))
              }
            />
          </div>
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Button URL"
              value={form.cta_card.button_url}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  cta_card: { ...prev.cta_card, button_url: e.target.value },
                }))
              }
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">FAQ card</h2>
        <input
          className="form-control"
          placeholder="Tag label"
          value={form.faq_card.tag}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              faq_card: { ...prev.faq_card, tag: e.target.value },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Heading"
          value={form.faq_card.heading}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              faq_card: { ...prev.faq_card, heading: e.target.value },
            }))
          }
        />
        <textarea
          className="form-control"
          rows="2"
          placeholder="Description"
          value={form.faq_card.description}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              faq_card: { ...prev.faq_card, description: e.target.value },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Search placeholder"
          value={form.faq_card.search_placeholder}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              faq_card: {
                ...prev.faq_card,
                search_placeholder: e.target.value,
              },
            }))
          }
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <h2 className="text-lg font-semibold text-slate-900 m-0">
            FAQ manager
          </h2>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowAddFaq((prev) => !prev)}
          >
            {showAddFaq ? "Close" : "Add FAQ"}
          </button>
        </div>

        {showAddFaq && (
          <div className="border rounded-lg p-3 space-y-2">
            <textarea
              className="form-control"
              rows="2"
              placeholder="Question"
              value={faqDraft.question}
              onChange={(e) =>
                setFaqDraft((prev) => ({ ...prev, question: e.target.value }))
              }
            />
            <textarea
              className="form-control"
              rows="3"
              placeholder="Answer"
              value={faqDraft.answer}
              onChange={(e) =>
                setFaqDraft((prev) => ({ ...prev, answer: e.target.value }))
              }
            />
            <div className="row g-2">
              <div className="col-md-4">
                <select
                  className="form-select"
                  value={faqDraft.category}
                  onChange={(e) =>
                    setFaqDraft((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                >
                  {categories
                    .filter((c) => c !== "all")
                    .map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-4">
                <input
                  className="form-control"
                  type="number"
                  placeholder="Sort order"
                  value={faqDraft.sort_order}
                  onChange={(e) =>
                    setFaqDraft((prev) => ({
                      ...prev,
                      sort_order: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="col-md-4 d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={faqDraft.is_active}
                  onChange={(e) =>
                    setFaqDraft((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={submitFaq}
                disabled={faqSaving}
              >
                {faqSaving ? "Saving..." : "Save FAQ"}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setShowAddFaq(false);
                  setFaqDraft(buildFaqDraft());
                }}
                disabled={faqSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <label className="text-sm text-slate-600">Filter by category:</label>
          <select
            className="form-select w-auto"
            value={faqCategoryFilter}
            onChange={(e) => setFaqCategoryFilter(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {faqLoading ? (
          <div className="text-sm text-slate-500">Loading FAQs...</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>#</th>
                  <th>Question</th>
                  <th style={{ width: "140px" }}>Category</th>
                  <th style={{ width: "120px" }}>Active</th>
                  <th style={{ width: "120px" }}>Sort</th>
                  <th style={{ width: "140px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq, idx) => (
                  <tr key={faq.id || faq._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{faq.question}</td>
                    <td>{faq.category || "general"}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={faq.is_active ?? false}
                        onChange={() => toggleFaqActive(faq)}
                        disabled={faqSaving}
                      />
                    </td>
                    <td>{faq.sort_order ?? 0}</td>
                    <td className="d-flex gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => startEditFaq(faq)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => deleteFaq(faq)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {faqEditId && (
          <div className="border rounded-lg p-3 space-y-2">
            <div className="text-sm font-semibold">Edit FAQ</div>
            <textarea
              className="form-control"
              rows="2"
              placeholder="Question"
              value={faqEditDraft.question}
              onChange={(e) =>
                setFaqEditDraft((prev) => ({
                  ...prev,
                  question: e.target.value,
                }))
              }
            />
            <textarea
              className="form-control"
              rows="3"
              placeholder="Answer"
              value={faqEditDraft.answer}
              onChange={(e) =>
                setFaqEditDraft((prev) => ({
                  ...prev,
                  answer: e.target.value,
                }))
              }
            />
            <div className="row g-2">
              <div className="col-md-4">
                <select
                  className="form-select"
                  value={faqEditDraft.category}
                  onChange={(e) =>
                    setFaqEditDraft((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                >
                  {categories
                    .filter((c) => c !== "all")
                    .map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-4">
                <input
                  className="form-control"
                  type="number"
                  placeholder="Sort order"
                  value={faqEditDraft.sort_order}
                  onChange={(e) =>
                    setFaqEditDraft((prev) => ({
                      ...prev,
                      sort_order: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="col-md-4 d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  checked={faqEditDraft.is_active}
                  onChange={(e) =>
                    setFaqEditDraft((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={updateFaq}
                disabled={faqSaving}
              >
                {faqSaving ? "Saving..." : "Save"}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={cancelEditFaq}
                disabled={faqSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">
          Compact newsletter card
        </h2>
        <input
          className="form-control"
          placeholder="Tag label"
          value={form.newsletter_card.compact.tag}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              newsletter_card: {
                ...prev.newsletter_card,
                compact: {
                  ...prev.newsletter_card.compact,
                  tag: e.target.value,
                },
              },
            }))
          }
        />
        <input
          className="form-control"
          placeholder="Title"
          value={form.newsletter_card.compact.title}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              newsletter_card: {
                ...prev.newsletter_card,
                compact: {
                  ...prev.newsletter_card.compact,
                  title: e.target.value,
                },
              },
            }))
          }
        />
        <textarea
          className="form-control"
          rows="2"
          placeholder="Description"
          value={form.newsletter_card.compact.description}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              newsletter_card: {
                ...prev.newsletter_card,
                compact: {
                  ...prev.newsletter_card.compact,
                  description: e.target.value,
                },
              },
            }))
          }
        />
        <div className="row g-2">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Placeholder"
              value={form.newsletter_card.compact.placeholder}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  newsletter_card: {
                    ...prev.newsletter_card,
                    compact: {
                      ...prev.newsletter_card.compact,
                      placeholder: e.target.value,
                    },
                  },
                }))
              }
            />
          </div>
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Button label"
              value={form.newsletter_card.compact.button_label}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  newsletter_card: {
                    ...prev.newsletter_card,
                    compact: {
                      ...prev.newsletter_card.compact,
                      button_label: e.target.value,
                    },
                  },
                }))
              }
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 m-0">
              Partner logos
            </h2>
            <p className="text-sm text-slate-500 mb-0">
              Manage the â€œAs seen withâ€ strip shown above the footer links.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={addPartner}
          >
            Add partner
          </button>
        </div>

        <div className="row g-2">
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Eyebrow"
              value={form.partners_section.eyebrow}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  partners_section: {
                    ...prev.partners_section,
                    eyebrow: e.target.value,
                  },
                }))
              }
            />
          </div>
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Heading"
              value={form.partners_section.heading}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  partners_section: {
                    ...prev.partners_section,
                    heading: e.target.value,
                  },
                }))
              }
            />
          </div>
          <div className="col-md-4">
            <input
              className="form-control"
              placeholder="Optional description"
              value={form.partners_section.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  partners_section: {
                    ...prev.partners_section,
                    description: e.target.value,
                  },
                }))
              }
            />
          </div>
        </div>

        <div
          className="rounded-4 border p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(250,246,237,0.98), rgba(241,237,228,0.94))",
            borderColor: "rgba(15, 23, 42, 0.08)",
          }}
        >
          <div className={`row g-4 align-items-center${previewHasPartnerCopy ? "" : " justify-content-center"}`}>
            {previewHasPartnerCopy ? (
              <div className="col-lg-4">
                <p className="text-uppercase text-muted small mb-2" style={{ letterSpacing: "0.28em" }}>
                  {form.partners_section.eyebrow || "Partners"}
                </p>
                <h3 className="mb-2" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "2rem", color: "#10233d" }}>
                  {form.partners_section.heading || "As seen with"}
                </h3>
                {form.partners_section.description ? (
                  <p className="mb-0 text-muted">{form.partners_section.description}</p>
                ) : null}
              </div>
            ) : null}
            <div className={previewHasPartnerCopy ? "col-lg-8" : "col-12"}>
              <div className={`d-flex flex-wrap gap-3${previewHasPartnerCopy ? "" : " justify-content-center"}`}>
                {normalizePartners(form.partners_section.items)
                  .filter((item) => item.is_active && (item.name || item.logo_url))
                  .map((item, index) => (
                    <div
                      key={`partner-preview-${index}`}
                      className="border bg-white d-flex align-items-center justify-content-center"
                      style={{
                        minWidth: "104px",
                        minHeight: "88px",
                        borderRadius: "1.5rem",
                        borderColor: "rgba(148, 163, 184, 0.28)",
                        boxShadow: "0 14px 28px rgba(15, 23, 42, 0.08)",
                      }}
                    >
                      {item.logo_url ? (
                        <img
                          src={resolveMediaUrl(item.logo_url)}
                          alt={item.alt_text || item.name || `Partner ${index + 1}`}
                          style={{ maxHeight: "36px", maxWidth: "108px", objectFit: "contain" }}
                        />
                      ) : (
                        <span className="fw-semibold text-slate-700">{item.name || "Partner"}</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {normalizePartners(form.partners_section.items).map((item, index) => (
            <div
              key={`partner-item-${index}`}
              className="rounded-4 border border-slate-200 p-3"
              style={{ background: "rgba(248, 250, 252, 0.75)" }}
            >
              <div className="d-flex flex-wrap gap-3 align-items-start justify-content-between">
                <div>
                  <div className="fw-semibold text-slate-900">
                    Partner #{index + 1}
                  </div>
                  <div className="text-sm text-slate-500">
                    Upload a logo or keep it text-only.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removePartner(index)}
                >
                  Remove
                </button>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-lg-3">
                  <label className="form-label">Partner name</label>
                  <input
                    className="form-control"
                    placeholder="Razorpay"
                    value={item.name}
                    onChange={(e) => updatePartner(index, { name: e.target.value })}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Partner URL</label>
                  <input
                    className="form-control"
                    placeholder="https://partner.com"
                    value={item.url}
                    onChange={(e) => updatePartner(index, { url: e.target.value })}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Alt text</label>
                  <input
                    className="form-control"
                    placeholder="Partner logo alt text"
                    value={item.alt_text}
                    onChange={(e) => updatePartner(index, { alt_text: e.target.value })}
                  />
                </div>
                <div className="col-lg-2">
                  <label className="form-label">Sort order</label>
                  <input
                    className="form-control"
                    type="number"
                    value={item.sort_order}
                    onChange={(e) =>
                      updatePartner(index, {
                        sort_order: Number(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="col-lg-1 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={item.is_active}
                      onChange={(e) =>
                        updatePartner(index, { is_active: e.target.checked })
                      }
                    />
                    <label className="form-check-label">Active</label>
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-1 align-items-end">
                <div className="col-lg-6">
                  <label className="form-label">Logo URL</label>
                  <input
                    className="form-control"
                    placeholder="/uploads/partner-logo.png"
                    value={item.logo_url}
                    onChange={(e) =>
                      updatePartner(index, { logo_url: e.target.value })
                    }
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label d-block">Upload logo</label>
                  <label className="btn btn-outline-secondary btn-sm mb-0">
                    {partnerUploadingIndex === index ? "Uploading..." : "Choose file"}
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      disabled={partnerUploadingIndex === index}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadPartnerLogo(index, file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
                <div className="col-lg-3">
                  <div className="small text-muted mb-2">Preview</div>
                  <div
                    className="rounded-4 border bg-white d-flex align-items-center justify-content-center"
                    style={{
                      minHeight: "76px",
                      borderColor: "rgba(148, 163, 184, 0.28)",
                    }}
                  >
                    {item.logo_url ? (
                      <img
                        src={resolveMediaUrl(item.logo_url)}
                        alt={item.alt_text || item.name || `Partner ${index + 1}`}
                        style={{ maxHeight: "34px", maxWidth: "128px", objectFit: "contain" }}
                      />
                    ) : (
                      <span className="text-sm text-slate-500 px-3 text-center">
                        Upload a logo
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Social links</h2>
        {Object.keys(form.social_links).map((key) => (
          <input
            key={key}
            className="form-control"
            placeholder={key}
            value={form.social_links[key] || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                social_links: {
                  ...prev.social_links,
                  [key]: e.target.value,
                },
              }))
            }
          />
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Bottom strip</h2>
        <input
          className="form-control"
          placeholder="Copyright text"
          value={form.copyright_text}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, copyright_text: e.target.value }))
          }
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <h2 className="text-lg font-semibold text-slate-900 m-0">
            Newsletter subscribers
          </h2>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => loadSubscribers(subscriberMeta.page || 1)}
            >
              Refresh
            </button>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={exportSubscribers}
            >
              Export CSV
            </button>
          </div>
        </div>
        {subscriberLoading ? (
          <div className="text-sm text-slate-500">Loading subscribers...</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Subscribed</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length ? (
                  subscribers.map((subscriber) => (
                    <tr key={subscriber.id}>
                      <td>{subscriber.email}</td>
                      <td>{subscriber.source}</td>
                      <td>
                        {subscriber.subscribed_at
                          ? new Date(subscriber.subscribed_at).toLocaleString()
                          : ""}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={subscriber.is_active}
                          onChange={() => toggleSubscriber(subscriber)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center text-slate-500 py-3">
                      No subscribers yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={subscriberMeta.page <= 1}
            onClick={() => loadSubscribers(subscriberMeta.page - 1)}
          >
            Previous
          </button>
          <div className="text-sm text-slate-600">
            Page {subscriberMeta.page} of {subscriberMeta.pages}
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={subscriberMeta.page >= subscriberMeta.pages}
            onClick={() => loadSubscribers(subscriberMeta.page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save Footer"}
        </button>
      </div>
    </div>
  );
};

export default ContentFooterPage;


